import React from 'react';
import { Component, Position, Container, Dispatcher, VBox } from 'puzzle';
import { setDocumentTitle, showSupplementaryFiles } from "../util/Util";
import { Splitter, SplitterOnChangeEvent } from "@progress/kendo-react-layout";
import MainGrid from "../MainGrid/MainGrid";
import DrawerHeader from "../Drawer/DrawerHeader/DrawerHeader";
import DrawerContent from "../Drawer/DrawerContent/DrawerContent";
import Drawer from "../Drawer/Drawer";
import { PREFERENCES_DATA } from "../Preferences/Types";
import * as _ from "lodash";
import { get } from "lodash";

type SUBMISSION_LIST_PROPS = {
  initializeUserData: any,
  location: LOCATION,
  title: string
};

type LOCATION = {
  search: string
}

type SUBMISSION_LIST_STATE = {
  detached: boolean,
  drawerOption: boolean,
  itemsLength: number,
  keyboardShortcuts: boolean
  panes: PANES[]
  preselectedDrawerTab: number,
  selectedRecord: any,
}

type PANES = {
  size?: string,
  min?: string,
  max?: string,
  resizable?: boolean,
  collapsed?: boolean
}

class SubmissionsList extends Component<SUBMISSION_LIST_PROPS, SUBMISSION_LIST_STATE> {
  /**
   * Map of drawer tabs.
   * Order is important! In order like is displayed on UI
   */
  DRAWER_TABS_MAP = [
    'STATUS',
    'DETAILS',
    'FILES',
    'QUOTES',
    'SUPPLEMENTARY_FILES',
  ];

  subDetailsRef: any = React.createRef();
  clientView = true;
  state: SUBMISSION_LIST_STATE = {
    detached: false,
    panes: [
      {},
      {
        size: '66%',
        min: '34%',
        max: '66%',
        resizable: false,
        collapsed: true
      }
    ],
    selectedRecord: {},
    drawerOption: false,
    keyboardShortcuts: false,
    preselectedDrawerTab: 0,
    itemsLength: 0
  };

  componentWillUnmount(): void {
    this.setState({ detached: false });
    super.componentWillUnmount();
  }

  /**
   * @inheritDoc
   */
  moduleDidMount = () => {
    setDocumentTitle(this.props.title);
    let queryString = this.props.location?.search;
    if (queryString) {
      const filter = {
        searchData: this.parseQueryString(queryString)
      };
      this.dispatch('openPrefilteredSubmissionList', Position.end, {
        path: '/view/submissionList',
        filter,
        decodeFilter: true
      });
    }
  };

  /**
   * Closes drawer
   */
  closeDrawer = () => {
    if (this.isDrawerDetached()) {
      //todo try to find better way for closing detached drawer
      let popUpWindow = get(Dispatcher, 'appModules.PopupWindow[0].popupWindow');
      popUpWindow.close();
    }

    const panes = { ...this.state.panes };
    panes[1].collapsed = true;
    panes[1].resizable = false;
    this.setState({
      detached: false,
      panes,
      selectedRecord: {},
      preselectedDrawerTab: 0
    });
  };

  /**
   * Detach drawer to separate window
   */
  detachDrawer = (detach: boolean) => {
    const panes = { ...this.state.panes };
    panes[1].collapsed = true;
    panes[1].resizable = false;
    this.setState({
      detached: detach,
      panes
    }, () => {
      this.dispatch('updateDrawerState', '', {
        currentModuleName: this.moduleName
      });
    });
  };

  /**
   * Get drawer tab index by tab name
   * @param tabName
   */
  getDrawerTabIndexByName = (tabName: string) => {
    const { selectedRecord } = this.state;
    let statuses = { taskStatus: selectedRecord.taskStatus, submissionStatus: selectedRecord.submissionStatus };
    const isSupplementaryTabVisible = showSupplementaryFiles(statuses);
    const drawerTabs = this.DRAWER_TABS_MAP;

    let unavailableTabs: string[] = [];
    if (!isSupplementaryTabVisible) {
      unavailableTabs.push('SUPPLEMENTARY_FILES');
    }

    if (!selectedRecord.quoteStatus) {
      unavailableTabs.push('QUOTES');
    }

    let filteredDrawerTabs = _.filter(drawerTabs, (item: string) => !unavailableTabs.includes(item));

    let index = filteredDrawerTabs.indexOf(tabName);

    return index;
  }

  /**
   * Returns content class name
   */
  getPageCss = (): string => {
    return 'tp-submission-view';
  };

  /**
   * Return page title
   */
  getPageTitle = () => {
    let title = this.translate('page-title');

    return title;
  }

  /**
   * Get tab which to preselect
   * @param quotesInView
   * @param filesInView
   */
  getTabForSelection = (quotesInView: boolean, filesInView: boolean): string => {
    return quotesInView ? 'QUOTES' : 'STATUS';
  }

  /**
   * Opens drawer on selection change on main grid
   * @param records - selected records
   * @param forceOpenDrawer - force drawer to open true/false
   * @param quotesInView - preselect quotes tab (user od submission list, view quotes action)
   * @param filesInView - preselect files tab (used on task list, complete dialog)
   */
  gridOnSelectionChange = (records: any[], forceOpenDrawer: boolean = false, quotesInView: boolean, filesInView: boolean) => {
    if (forceOpenDrawer) {
      const { detached, drawerOption } = this.state;
      const drawerState = drawerOption ? drawerOption : detached;
      const panes = { ..._.cloneDeep(this.state.panes) };
      panes[1].collapsed = drawerOption ? drawerOption : detached;
      panes[1].resizable = !detached;
      this.setState({
        selectedRecord: records[0],
        detached: drawerState,
      }, () => {
        let tabName: string = this.getTabForSelection(quotesInView, filesInView);
        this.setPreselectedTabByName(tabName, { panes });
      });
      this.dispatch('checkboxSelectionClear', Position.end);
    } else {
      if (records.length === 1) {
        this.setState({
          selectedRecord: records[0]
        }, () => {
          let tabName: string = this.getTabForSelection(quotesInView, filesInView);
          this.setPreselectedTabByName(tabName, {});
        });
      } else this.closeDrawer()
    }
  };

  handleDrawerResize = () => {
    this.dispatch('onDrawerResize');
  };

  isDrawerOpen = (): boolean => {
    const drawerDetached: boolean = this.isDrawerDetached();
    // @ts-ignore
    const drawerClosed: boolean = this.state.panes[1].collapsed;
    return drawerDetached || !drawerClosed;
  };

  /**
   * returns is drawer open in grid
   * */
  getDrawerState = (): boolean => !this.state.panes[1].collapsed;


  isDrawerDetached = (): boolean => {
    return this.state.detached;
  };

  /**
   * Attach detached drawer, or close it if attachDrawer arg is false
   */
  newWindowUnloaded = (attachDrawer: boolean = true) => {
    /*if (window) {
      window.removeEventListener("resize", this.handleDrawerResize);
    }*/
    const { drawerOption } = this.state
    if (this.isDrawerDetached()) {
      if (attachDrawer) {
        const panes = { ...this.state.panes };
        panes[1].collapsed = drawerOption;
        panes[1].resizable = !drawerOption;
        this.setState({ detached: false, panes }, () => {
          this.dispatch('updateDrawerDetachedState', '', { detached: false });
        });
      } else this.closeDrawer()
    }
  };

  /**
   * Updates state with new slider data
   */
  onLayoutChange = (event: SplitterOnChangeEvent) => {
    if (event) {
      this.setState({ panes: event.newState });
      this.subDetailsRef?.classList?.remove('wrap');
      if (this.subDetailsRef?.offsetWidth < 1100) {
        this.subDetailsRef?.classList?.add('wrap');
      }
      this.dispatch('handleDrawerResize', { buffer: 300 })
    }
  };

  /**
   * Creates object from query string and returns it stringified
   * note: queryString starts with '?'
   * @param queryString
   */
  parseQueryString = (queryString: string): string => {
    queryString = queryString.slice(1);

    const pairs = queryString.split('&');
    const result = {};
    let pair, key, value;

    pairs.forEach((p: string) => {
      pair = p.split('=');
      key = pair[0];
      if (key) {
        value = decodeURIComponent(pair[1] || '');

        switch (key) {
          case 'sourceLanguageValues':
          case 'targetLanguageValues':
          case 'phaseNameValues':
          case 'submissionStatusValues':
          case 'submissionOwnerValues':
          case 'quoteStatuses':
            // case 'awardedVendorsCombo':
            // case 'vendorsCombo':
            // case 'quoteStatuses':
            // case 'phaseStatuses':
            (result as any)[key] = value.split(',');
            break;
          case 'fromDateCreated':
          case 'toDateCreated':
          case 'fromDueDate':
          case 'toDueDate':
            // case 'fromPhaseDueDate':
            // case 'toPhaseDueDate':
            (result as any)[key] = parseInt(value);
            break;
          case 'projectNameInputText':
          case 'submissionNameInputText':
          case 'fileNameInputText':
            (result as any)[key] = value;
            break;
          default:
            break;
        }
      }
    });
    return JSON.stringify(result);
  };

  /**
   * Set preferences data to state (keyboardShortcuts and drawerOption)
   */
  setPreferencesData = (preferencesData: PREFERENCES_DATA) => {
    const { keyboardShortcuts, drawerOption } = preferencesData;
    this.setState({ keyboardShortcuts, drawerOption });
  };

  /**
   * Recalculates and sets height of the grid in Files Tab of drawer
   */
  setFilesTabHeight = () => {
    this.dispatch('updateFilesGridHeight');
  };

  /**
   * Sets reference to the submission details tab placed in drawer
   */
  setSubmissionDetailsTabRef = (ref: any) => {
    this.subDetailsRef = ref;
  }

  /**
   * Set preselected tab by tab name
   * @param tabName
   * @param additionalData - additionalData for setState. Used to set drawer open in same time as preselectedDrawerTab
   */
  setPreselectedTabByName = (tabName: string, additionalData: any) => {
    this.setState({
      preselectedDrawerTab: this.getDrawerTabIndexByName(tabName),
      ...additionalData
    });
  }

  /**
   * Update selected record after grid get new data
   */
  updateSelectedRecordAfterGridUpdate = (gridData: any) => {
    const { selectedRecord: oldSelectedRecord } = this.state;
    const { items, tasks } = gridData;
    let selectedRecord = _.find(items, { 'submissionId': oldSelectedRecord.submissionId });
    if (selectedRecord) {
      selectedRecord.tasks = tasks;
      this.setState({
        selectedRecord
      });
    }
  }

  /**
   * If selected record is (un)favored,
   * update selectedRecord in the state and so the drawer
   */
  updateSelectedRecordAfterFavoriteChange = (record: any) => {
    const { selectedRecord } = this.state;
    if (selectedRecord && record.submissionId === selectedRecord.submissionId) {
      this.setState({ selectedRecord: record })
    }
  };

  /**
   *
   */
  renderContent = () => {
    const { detached } = this.state;
    const {
      keyboardShortcuts,
      drawerOption
    } = this.state;
    const preferencesData = { keyboardShortcuts, drawerOption }

    return (
      <MainGrid
        checkboxSelection={true}
        detached={detached}
        gridSectionAriaLabel={this.translate('SRL-section-table')}
        topSectionAriaLabel={this.translate('SRL-section-actions')}
        updateDrawerState={this.detachDrawer}
        emptyGridConfig={{
          text: this.translate('gridEmptyLabel'),
          img: 'subs-grid'
        }}
        quickFiltersEnabled={true}
        searchBarConfig={{
          pageTitle: this.translate('page-title'),
          pageTitleCount: (count: number) => this.translate('page-title-count', { count }),
          isClientView: this.clientView,
          initializeUserData: this.props.initializeUserData,
        }}
        title={this.translate('grid-title')}
        {...preferencesData}
      />)
  }

  /**
   * Drawer render
   */
  renderDrawer = () => {
    const {
      detached,
      preselectedDrawerTab,
      selectedRecord
    } = this.state;
    const {
      initializeUserData
    } = this.props;

    return <Drawer
      header={
        <DrawerHeader
          updateDrawerState={this.detachDrawer}
          submissionData={selectedRecord}
        />
      }
      drawerContent={
        <DrawerContent
          setSubmissionDetailsTabRef={this.setSubmissionDetailsTabRef}
          data={selectedRecord}
          detached={detached}
          initializeUserData={initializeUserData}
          tabsMap={this.DRAWER_TABS_MAP}
          {...(preselectedDrawerTab && { preselectedDrawerTab })}
        />
      }
    />
  }

  renderBody() {
    const {
      detached,
      selectedRecord,
      preselectedDrawerTab
    } = this.state;
    const {
      initializeUserData
    } = this.props;
    const isDrawerOpen = this.isDrawerOpen();
    const quotesInView = preselectedDrawerTab === 3;

    return (
      <Container className={`tp-page ${this.getPageCss() + '-page'} ${isDrawerOpen ? 'tp-drawer-opened' : ''}`}>
        <Splitter
          className={'tp-no-border'}
          panes={this.state.panes}
          onChange={this.onLayoutChange}
        >
          <VBox className={`${this.getPageCss()}`}>
            {this.renderContent()}
          </VBox>
          {this.renderDrawer()}
        </Splitter>
        {detached && this.dispatch('openDrawerPopupWindow', 'end', {
          currentModuleName: this.moduleName,
          tabsMap: this.DRAWER_TABS_MAP,
          selectedRecord,
          initializeUserData,
          quotesInView,
          detached,
          preselectedDrawerTab
        })}
      </Container>
    );
  }
}

export default SubmissionsList;
